<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import ProgressSpinner from 'primevue/progressspinner';
import { useEndpoint } from '@apparatix/composables';
import { Section } from '@apparatix/library/core';
import AssociationListContainer from '@apparatix/leads/AssociationListContainer.vue';
import InventoryRelationshipItem from './InventoryRelationshipItem.vue';
import type { InventoryRelationship } from './types';

const props = defineProps<{ id: number }>();

const relationship = ref<InventoryRelationship | null>(null);

const { isLoading, makeRequest, setEndpointUrl } = useEndpoint<{ data: InventoryRelationship }>({
  url: '',
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    relationship.value = response.data;
  },
  toast: { showOnSuccess: false },
});

const lease = computed(() => relationship.value?.lease ?? null);
const hasStructure = computed(() => !!relationship.value?.structureNumber);

// The flyout reuses this component instance across inventory ids without
// remounting, so refetch on every props.id change rather than only onMounted.
const loadRelationships = () => {
  relationship.value = null;
  setEndpointUrl(`/manage-measurements/inventory/${props.id}/relationships`);
  makeRequest();
};

watch(() => props.id, loadRelationships);
onMounted(loadRelationships);
</script>
<template>
  <div class="flex flex-col gap-6">
    <div v-if="isLoading" class="flex justify-center py-10">
      <ProgressSpinner style="width: 2.5rem; height: 2.5rem" />
    </div>
    <template v-else-if="relationship">
      <Section :footer="false">
        <AssociationListContainer
          title="Structure"
          :item-count="hasStructure ? 1 : 0"
          :hide-add-button="true"
          no-items-message="No structure on file."
        >
          <InventoryRelationshipItem
            v-if="hasStructure"
            :title="relationship.structureNumber as string"
            subtitle="Structure"
            icon="pi pi-building"
            icon-background="var(--p-indigo-100)"
            icon-color="var(--p-indigo-500)"
          />
        </AssociationListContainer>
      </Section>

      <Section :footer="false">
        <AssociationListContainer
          title="Lease"
          :item-count="lease ? 1 : 0"
          :hide-add-button="true"
          no-items-message="This inventory unit is not associated with a lease."
        >
          <InventoryRelationshipItem
            v-if="lease"
            :title="lease.leaseNumber ?? 'Lease'"
            :subtitle="[lease.status, lease.type].filter(Boolean).join(' • ') || undefined"
            icon="pi pi-file"
            icon-background="var(--p-amber-100)"
            icon-color="var(--p-amber-600)"
          />
        </AssociationListContainer>
      </Section>
    </template>
  </div>
</template>
